import React, { useEffect } from 'react'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import {
  Spinner,
  SkeletonLoader,
  ThumbContainer,
  ThumbContainerHeader,
  ThumbDefaultOverlay,
  ThumbDefaultError,
  Thumb,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_SMALL_LIGHT,
  Flex
} from 'mailup-web-components'

import {
  pageState,
  previewCardState,
  previewModalState,
  previewsPollingState
} from 'Pages/EmailMessagePreview/recoil/state/page'

import { retryPreviewsCallback } from 'Pages/EmailMessagePreview/recoil/callbacks/callbacks'

const PreviewCard = ({ previewId }) => {
  const { id } = useParams()

  const [
    {
      displayName,
      operativeSystem,
      browser,
      status,
      thumbnailImagePath,
      previewImagePath,
      loaded,
      imageLoading
    },
    setCardState
  ] = useRecoilState(previewCardState(previewId))
  const setModal = useSetRecoilState(previewModalState)
  const { view } = useRecoilValue(pageState)
  const { pollingIteration } = useRecoilValue(previewsPollingState)
  const retryCallback = useRecoilCallback(retryPreviewsCallback, [])

  const openPreview = () => {
    setModal(prev => ({
      ...prev,
      show: true,
      displayName: displayName,
      browser: browser,
      operativeSystem: operativeSystem,
      previewImagePath: previewImagePath
    }))
  }

  const handleRetry = () => {
    retryCallback(id, view)
  }

  let attemptN = 0
  const handleImageError = e => {
    if (attemptN < 20) {
      e.target.onerror = null
      e.target.src = { previewImagePath }
      attemptN += 1
    }
  }

  const handleImageLoaded = () => {
    setCardState(prev => ({
      ...prev,
      imageLoading: false
    }))
  }

  useEffect(() => {
    if (status !== 'Complete' && pollingIteration >= 20) {
      setCardState(prev => ({
        ...prev,
        status: 'Error'
      }))
    }
  }, [pollingIteration, status])

  return (
    <ThumbContainer>
      <ThumbContainerHeader>
        <Flex
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          flexGrow={1}
          padding='8px 24px'
        >
          <Text variant={TEXT_VARIANT_BOLD}>
            {!displayName && <SkeletonLoader width='200px' />}
            {displayName && displayName}
          </Text>
          <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
            {!operativeSystem && <SkeletonLoader width='160px' />}
            {operativeSystem && operativeSystem} {browser && browser}
          </Text>
        </Flex>
      </ThumbContainerHeader>
      <StyledThumb
        data-mu-test={`thumbnail-preview-${previewId}`}
        aspectRatio={0}
        HoverComponent={status !== 'Error' ? Overlay : undefined}
        onClick={status !== 'Error' ? openPreview : undefined}
      >
        {loaded && status && status === 'Complete' && (
          <>
            <img
              style={{ display: imageLoading ? 'none' : 'block' }}
              src={thumbnailImagePath}
              alt={displayName}
              onLoad={handleImageLoaded}
              onError={e => handleImageError(e)}
            />
          </>
        )}
        {(!loaded ||
          imageLoading ||
          (status && (status === 'Pending' || status === 'Processing'))) && (
          <Spinner />
        )}
        {status && status === 'Error' && <ContentError onClick={handleRetry} />}
      </StyledThumb>
    </ThumbContainer>
  )
}

export default PreviewCard

const Overlay = () => {
  const intl = useIntl()

  return (
    <ThumbDefaultOverlay>
      {intl.formatMessage({ id: 'Load.MailupCommon.PreviewOpen' })}
    </ThumbDefaultOverlay>
  )
}

const ContentError = ({ onClick }) => {
  const intl = useIntl()
  return (
    <ThumbDefaultError onClick={onClick}>
      {intl.formatMessage({ id: 'Load.MailupCommon.PreviewRefresh' })}
    </ThumbDefaultError>
  )
}

const StyledThumb = styled(Thumb)`
  height: 250px;
  aspect-radio: none;

  img {
    overflow: hidden;
  }
`

ContentError.propTypes = {
  onClick: PropTypes.func
}

PreviewCard.propTypes = {
  previewId: PropTypes.any
}
